import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, CopyCheck, HardDriveDownload, Plus } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import type { StagedRoast, StagedStatus } from '../hooks/useArtisanStaging';

interface Props {
    items: StagedRoast[];
    onSelectProfile: (id: string) => void;
    onUploadMore: () => void;
    onDone: () => void;
}

const STATUS_STYLES: Record<string, string> = {
    saved: 'bg-green-100 text-green-800',
    duplicate: 'bg-blue-100 text-blue-800',
    invalid: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
};

const StatusIcon: React.FC<{ status: StagedStatus }> = ({ status }) => {
    const className = 'h-3.5 w-3.5';
    if (status === 'saved') return <CheckCircle2 className={className} />;
    if (status === 'duplicate') return <CopyCheck className={className} />;
    if (status === 'invalid') return <AlertTriangle className={className} />;
    return <XCircle className={className} />;
};

/** What happened to each roast in the batch, once the save has run. */
export const ArtisanUploadResults: React.FC<Props> = ({ items, onSelectProfile, onUploadMore, onDone }) => {
    const { t } = useLanguage();

    const saved = items.filter(i => i.status === 'saved').length;
    const duplicate = items.filter(i => i.status === 'duplicate').length;
    const notSaved = items.filter(i => i.status === 'failed' || i.status === 'invalid').length;
    const anyStoredNothing = items.some(i => i.status === 'saved' && i.fileStored === false);

    const detailFor = (item: StagedRoast) => {
        if (item.status === 'failed') return item.failure || '-';
        if (item.status === 'invalid') return t(`artisanSync.stage.reason.${item.errorCode ?? 'parse'}`);
        // A duplicate with no id has nothing to add: the status pill already says it.
        return '-';
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">{t('artisanSync.stage.resultsTitle')}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {t('artisanSync.bulk.summary', { created: saved, duplicate, failed: notSaved })}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={onUploadMore}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium"
                        data-test-id="artisan-upload-more"
                    >
                        <Plus className="h-4 w-4" />
                        {t('artisanSync.stage.uploadMore')}
                    </button>
                    <button
                        onClick={onDone}
                        className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium shadow-sm"
                        data-test-id="artisan-bulk-done"
                    >
                        {t('artisanSync.bulk.done')}
                    </button>
                </div>
            </div>

            {anyStoredNothing && (
                <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg flex items-start gap-3 text-sm">
                    <HardDriveDownload className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <p>{t('artisanSync.file.notStoredBatch')}</p>
                </div>
            )}

            <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200" data-test-id="artisan-bulk-results">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('artisanSync.bulk.fileName')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('artisanSync.bulk.status')}</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('artisanSync.bulk.detail')}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {items.map(item => (
                                <tr key={item.id} data-test-id="artisan-bulk-result-row">
                                    <td className="px-6 py-3 text-sm text-gray-900 font-medium break-all">{item.fileName}</td>
                                    <td className="px-6 py-3 whitespace-nowrap">
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[item.status] ?? 'bg-gray-100 text-gray-700'}`}>
                                            <StatusIcon status={item.status} />
                                            {t(`artisanSync.stage.status.${item.status}`)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 text-sm text-gray-500">
                                        {item.profileId ? (
                                            <button
                                                className="text-primary-600 hover:text-primary-900 font-medium"
                                                onClick={() => onSelectProfile(item.profileId!)}
                                            >
                                                {t('artisanSync.bulk.viewProfile')}
                                            </button>
                                        ) : (
                                            detailFor(item)
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
